import React from 'react';

import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth/types';
import { LIST_HOME_STATISTICS_RESOURCE } from 'src/features/home/api/useListHomeStatistics';
import { STAYS_PANEL_NAME } from 'src/features/home/components/WidgetEntries';
import { PATH } from 'src/features/navigation/utils/constants';
import { useEndActiveStay } from 'src/features/stay/api/useEndActiveStay';
import { LIST_ACTIVE_STAYS_RESOURCE } from 'src/features/stay/api/useListActiveStays';
import { ACTIVE_STAYS_PANEL_NAME } from 'src/features/stay/components/ActiveStaysTable';
import ExitFromParkingModal from 'src/features/stay/components/forms/ExitFromParkingModal';
import { ActiveStay } from 'src/features/stay/types';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { generalErrorMessages } from 'src/registry/utils/messages';

type ExitFromParkingFormModalProps = {
    activeStay: ActiveStay;
    children?: React.ReactNode;
};

const ExitFromParkingFormModal = ({ activeStay, children }: ExitFromParkingFormModalProps) => {
    const { formatMessage } = useIntl();
    const { closePanel } = useSidePanel();
    const navigate = useNavigate();

    const { stayId } = useParams();
    const { parkingId } = useParking();

    const permissionPastStays = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);

    const { mutate: endActiveStay, isPending } = useEndActiveStay({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_HOME_STATISTICS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_ACTIVE_STAYS_RESOURCE] });

            closePanel(ACTIVE_STAYS_PANEL_NAME);
            closePanel(STAYS_PANEL_NAME);

            if (stayId && success?.past_stay_id && permissionPastStays) {
                void navigate(`/${PATH.VISITORS}/past-stay/dashboard/${success?.past_stay_id}`, { replace: true });
            }

            toast({
                title: formatMessage(visitorMessages.exitFromParkingSuccess),
                description: formatMessage(visitorMessages.exitFromParkingSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ExitFromParkingModal
            activeStay={activeStay}
            isPending={isPending}
            mutate={(values, onSuccess, onError) =>
                endActiveStay({ ...values, stayId: activeStay.id, parkingId }, { onSuccess, onError })
            }
        >
            {children}
        </ExitFromParkingModal>
    );
};

export default ExitFromParkingFormModal;
